import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Calendar,
  Mail,
  Users,
  Wallet,
  BookOpen,
  Scale,
  ShieldCheck,
  FileSearch,
  ArrowUpRight,
  Loader2,
  Sparkles,
  Send,
  BarChart3,
  Instagram,
  ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const STAT_ICON = {
  bookings: Calendar,
  revenue: Wallet,
  contacts: Mail,
  users: Users,
};

function StatCard({ icon: Icon, label, value, sub, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6"
    >
      <div className="flex items-center justify-between">
        <p className="eyebrow">{label}</p>
        <Icon className={`w-4 h-4 ${accent || "text-slate-500"}`} />
      </div>
      <p className="font-display text-4xl mt-4 tracking-tight">{value}</p>
      {sub ? (
        <p className="text-xs text-slate-500 mt-2 leading-relaxed">{sub}</p>
      ) : null}
    </div>
  );
}

function NavCard({ to, eyebrow, title, lead, icon: Icon, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 lg:p-7 hover:border-slate-900 dark:hover:border-amber-500 transition-colors block"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="eyebrow">{eyebrow}</p>
          <h3 className="font-display text-2xl mt-2 leading-tight">{title}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-3 leading-relaxed">
            {lead}
          </p>
        </div>
        <span className="shrink-0 w-10 h-10 rounded-sm border border-slate-200 dark:border-slate-800 flex items-center justify-center text-slate-500 group-hover:text-slate-900 dark:group-hover:text-amber-500 transition-colors">
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <div className="mt-6 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-slate-500 group-hover:text-slate-900 dark:group-hover:text-amber-500 transition-colors">
        <span>{eyebrow}</span>
        <ArrowUpRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}

export default function AdminDashboard() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => setErr(true));
  }, [user]);

  const t = useMemo(
    () => ({
      eyebrow: isAr ? "لوحة التحكم" : "Admin overview",
      title: isAr ? "إدارة المكتب." : "Run the firm.",
      lead: isAr
        ? "نظرة عامة على الحجوزات والاستفسارات والعملاء — كل ما يحتاجه فريق الدخيل لإدارة العمل اليومي."
        : "A real-time view of bookings, inquiries and clients — everything the Aldakheel team needs to run the day.",
      bookings: isAr ? "الحجوزات" : "Bookings",
      bookingsSub: (s) =>
        isAr
          ? `${s.paid} مدفوع · ${s.pending} قيد الدفع · ${s.today} اليوم`
          : `${s.paid} paid · ${s.pending} pending · ${s.today} today`,
      revenue: isAr ? "الإيرادات (د.ك)" : "Revenue (KWD)",
      revenueSub: (s) =>
        isAr
          ? `${s.paid} استشارة مدفوعة × ${stats?.fee_kwd ?? 50} د.ك`
          : `${s.paid} paid consultations × ${stats?.fee_kwd ?? 50} KWD`,
      contacts: isAr ? "الاستفسارات" : "Inquiries",
      contactsSub: (s) =>
        isAr
          ? `${s.new} جديدة في الانتظار`
          : `${s.new} new awaiting reply`,
      users: isAr ? "المستخدمون" : "Users",
      usersSub: (s) =>
        isAr
          ? `${s.clients} عميل · ${s.staff} فريق`
          : `${s.clients} clients · ${s.staff} staff`,
      navTitle: isAr ? "أقسام الإدارة" : "Workspaces",
      navLead: isAr
        ? "ادخل إلى كل قسم لإدارة بياناته."
        : "Dive into each workspace to manage records.",
      cards: {
        bookings: {
          title: isAr ? "الحجوزات" : "Consultation bookings",
          lead: isAr
            ? "عرض، تصفية، وحالة دفع كل استشارة."
            : "Browse, filter and track every paid consultation.",
        },
        contacts: {
          title: isAr ? "الاستفسارات" : "Contact inquiries",
          lead: isAr
            ? "الرسائل القادمة من نموذج التواصل."
            : "Messages from the website contact form.",
        },
        staff: {
          title: isAr ? "الفريق" : "Team",
          lead: isAr
            ? "أنشئ حسابات الموظفين وأدر الصلاحيات."
            : "Provision staff accounts and roles.",
        },
        training: {
          title: isAr ? "المعرفة" : "Training data",
          lead: isAr
            ? "أضف مذكرات وملفات لتطوير سامي."
            : "Curate Sami's memos and reference set.",
        },
        laws: {
          title: isAr ? "القوانين" : "Laws library",
          lead: isAr
            ? "إدارة قوانين V9 ومقالاتها."
            : "Maintain the V9 statutes library.",
        },
        research: {
          title: isAr ? "الأبحاث" : "Research vault",
          lead: isAr
            ? "أرشيف الأبحاث القانونية الداخلية."
            : "Internal legal research archive.",
        },
        judgments: {
          title: isAr ? "الأحكام" : "Judgments",
          lead: isAr
            ? "أرشيف الأحكام والسوابق."
            : "Archive of precedents and judgments.",
        },
      },
      err: isAr ? "تعذّر تحميل الإحصائيات." : "Could not load stats.",
      kwd: isAr ? "د.ك" : "KWD",
    }),
    [isAr, stats]
  );

  if (loading) {
    return (
      <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>
    );
  }
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const s = stats || {
    bookings: { total: 0, paid: 0, pending: 0, today: 0, revenue_kwd: 0 },
    contacts: { total: 0, new: 0 },
    users: { total: 0, clients: 0, staff: 0 },
    laws: { total: 0 },
    law_queue: { total: 0 },
    fee_kwd: 50,
  };

  return (
    <div
      data-testid="admin-dashboard-page"
      className="max-w-7xl mx-auto px-6 lg:px-10 py-20"
    >
      {/* HERO */}
      <div className="grid lg:grid-cols-12 gap-12 mb-14">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {t.lead}
          </p>
          <p className="mt-3 text-xs uppercase tracking-[0.2em] text-slate-500">
            {isAr ? "مرحباً" : "Signed in as"} ·{" "}
            <span className="text-slate-900 dark:text-slate-100">
              {user.name}
            </span>
          </p>
        </div>
      </div>

      {/* STATS */}
      {err ? (
        <div className="border border-red-200 bg-red-50 text-red-700 p-6 text-sm">
          {t.err}
        </div>
      ) : !stats ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-8">
          <Loader2 className="w-4 h-4 animate-spin" />
          {isAr ? "جارٍ التحميل…" : "Loading…"}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          <StatCard
            testid="stat-bookings"
            icon={STAT_ICON.bookings}
            label={t.bookings}
            value={s.bookings.total}
            sub={t.bookingsSub(s.bookings)}
            accent="text-amber-600"
          />
          <StatCard
            testid="stat-revenue"
            icon={STAT_ICON.revenue}
            label={t.revenue}
            value={`${s.bookings.revenue_kwd.toLocaleString()}`}
            sub={t.revenueSub(s.bookings)}
            accent="text-emerald-600"
          />
          <StatCard
            testid="stat-contacts"
            icon={STAT_ICON.contacts}
            label={t.contacts}
            value={s.contacts.total}
            sub={t.contactsSub(s.contacts)}
            accent="text-sky-600"
          />
          <StatCard
            testid="stat-users"
            icon={STAT_ICON.users}
            label={t.users}
            value={s.users.total}
            sub={t.usersSub(s.users)}
            accent="text-violet-600"
          />
          <StatCard
            testid="stat-laws"
            icon={Scale}
            label={isAr ? "القوانين المنشورة" : "Published laws"}
            value={(s.laws?.total ?? 0).toLocaleString(isAr ? "ar" : undefined)}
            sub={isAr ? "في المكتبة القانونية" : "in the legal library"}
            accent="text-amber-700"
          />
          <StatCard
            testid="stat-law-queue"
            icon={ScrollText}
            label={isAr ? "طابور مراجعة القوانين" : "Law review queue"}
            value={(s.law_queue?.total ?? 0).toLocaleString(isAr ? "ar" : undefined)}
            sub={isAr ? "بانتظار المراجعة والاعتماد" : "awaiting review & approval"}
            accent="text-rose-600"
          />
        </div>
      )}

      {/* NAV CARDS */}
      <div className="mb-8">
        <p className="eyebrow">{t.navTitle}</p>
        <h2 className="font-display text-3xl mt-2 tracking-tight">
          {t.navLead}
        </h2>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <NavCard
          testid="nav-bookings"
          to="/admin/bookings"
          eyebrow={isAr ? "حجوزات" : "Bookings"}
          title={t.cards.bookings.title}
          lead={t.cards.bookings.lead}
          icon={Calendar}
        />
        <NavCard
          testid="nav-contacts"
          to="/admin/contacts"
          eyebrow={isAr ? "استفسارات" : "Inquiries"}
          title={t.cards.contacts.title}
          lead={t.cards.contacts.lead}
          icon={Mail}
        />
        <NavCard
          testid="nav-staff"
          to="/admin/staff"
          eyebrow={isAr ? "فريق" : "Team"}
          title={t.cards.staff.title}
          lead={t.cards.staff.lead}
          icon={ShieldCheck}
        />
        <NavCard
          testid="nav-training"
          to="/admin/training"
          eyebrow={isAr ? "معرفة" : "Training"}
          title={t.cards.training.title}
          lead={t.cards.training.lead}
          icon={BookOpen}
        />
        <NavCard
          testid="nav-laws"
          to="/admin/laws"
          eyebrow={isAr ? "قوانين" : "Laws"}
          title={t.cards.laws.title}
          lead={t.cards.laws.lead}
          icon={Scale}
        />
        <NavCard
          testid="nav-law-queue"
          to="/admin/law-queue"
          eyebrow={isAr ? "استيراد رسمي" : "Ingestion"}
          title={isAr ? "طابور مراجعة القوانين" : "Law review queue"}
          lead={isAr
            ? "استورد قوانين رسمية بـ OCR، راجعها، ثم انشرها في المكتبة بضغطة."
            : "OCR-ingest official laws, review, then publish to the library."}
          icon={ScrollText}
        />
        <NavCard
          testid="nav-research"
          to="/admin/research"
          eyebrow={isAr ? "أبحاث" : "Research"}
          title={t.cards.research.title}
          lead={t.cards.research.lead}
          icon={FileSearch}
        />
        <NavCard
          testid="nav-judgments"
          to="/admin/judgments"
          eyebrow={isAr ? "أحكام" : "Judgments"}
          title={t.cards.judgments.title}
          lead={t.cards.judgments.lead}
          icon={Scale}
        />
        <NavCard
          testid="nav-analytics"
          to="/admin/analytics"
          eyebrow={isAr ? "تحليلات" : "Analytics"}
          title={isAr ? "التحليلات والمستخدمون" : "Analytics & users"}
          lead={isAr
            ? "أسئلة سامي · زوار الموقع · إدارة الحسابات وإعادة تعيين كلمات المرور."
            : "Sami questions · site visitors · manage accounts & reset passwords."}
          icon={BarChart3}
        />
        <NavCard
          testid="nav-social-topics"
          to="/admin/social-topics"
          eyebrow={isAr ? "سوشل ميديا" : "Social"}
          title={isAr ? "أفكار إنستغرام جاهزة" : "Instagram briefs"}
          lead={isAr
            ? "ولّد 15 موضوعاً قانونياً جاهزاً للنشر — لك وحدك."
            : "Generate 15 ready-to-record Kuwaiti-law briefs — admin-only."}
          icon={Instagram}
        />
        <NavCard
          testid="nav-blog-drafts"
          to="/admin/blog-drafts"
          eyebrow={isAr ? "المدوّنة" : "Blog"}
          title={isAr ? "مسوّدات المقالات" : "Article drafts"}
          lead={isAr
            ? "راجع المقالات المُولَّدة بالذكاء الاصطناعي وانشرها على /blog بضغطة."
            : "Review AI-generated articles and publish them to /blog with one click."}
          icon={ScrollText}
        />
      </div>

      {/* AI Article Generator */}
      <AIArticleGenerator isAr={isAr} />
    </div>
  );
}

function AIArticleGenerator({ isAr }) {
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (topic.trim().length < 6) {
      toast.error(isAr ? "اكتب موضوعاً أوضح" : "Topic too short");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/blog/generate", {
        topic, category: category || undefined,
      });
      setResult(r.data);
      toast.success(isAr ? "تم إنشاء مسودة" : "Draft generated");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        (isAr ? "تعذّر التوليد" : "Generation failed")
      );
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    if (!result?.id) return;
    setBusy(true);
    try {
      await api.patch(`/admin/blog/${result.id}/publish`);
      toast.success(isAr ? "تم النشر" : "Published");
      setResult({ ...result, is_published: true });
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-16 border p-7 lg:p-8 bg-white" style={{ borderColor: "var(--line)" }}
         data-testid="ai-article-generator">
      <div className="flex items-start gap-4">
        <span className="shrink-0 w-10 h-10 rounded-sm border grid place-items-center"
              style={{ borderColor: "var(--gold-deep)", color: "var(--gold-deep)" }}>
          <Sparkles className="w-4 h-4" />
        </span>
        <div className="flex-1">
          <p className="eyebrow">{isAr ? "مولّد المقالات الذكي" : "AI article generator"}</p>
          <h3 className="font-display text-2xl mt-1">
            {isAr ? "اكتب موضوعاً، يكتب سامي المقال." : "Type a topic, Sami writes the article."}
          </h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            {isAr
              ? "ينشئ مقالاً عربياً قانونياً مفصّلاً يُحفظ كمسودة في المدونة. راجعه ثم انشره بضغطة زر."
              : "Drafts a full Arabic legal article and saves it as a blog draft. Review then publish in one click."}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        <input
          dir="auto"
          data-testid="ai-article-topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder={isAr ? "مثال: شروط فسخ عقد العمل في الكويت" : "e.g. terminating an employment contract"}
          className="h-11 px-4 rounded-sm border text-sm"
          style={{ borderColor: "var(--line-strong)" }}
        />
        <input
          dir="auto"
          data-testid="ai-article-category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder={isAr ? "التصنيف (اختياري): قانون العمل" : "Category (optional): Labor Law"}
          className="h-11 px-4 rounded-sm border text-sm"
          style={{ borderColor: "var(--line-strong)" }}
        />
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          data-testid="ai-article-generate"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-sm text-sm btn-gold disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {isAr ? "أنشئ مسودة" : "Generate draft"}
        </button>
        {result && !result.is_published && (
          <button
            type="button"
            onClick={publish}
            disabled={busy}
            data-testid="ai-article-publish"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-sm text-sm"
            style={{ background: "var(--navy)", color: "#fff" }}
          >
            <Send className="w-4 h-4" />
            {isAr ? "انشر فوراً" : "Publish now"}
          </button>
        )}
        {result?.is_published && (
          <Link
            to={`/blog/${result.slug}`}
            target="_blank"
            data-testid="ai-article-view"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-sm text-sm border"
            style={{ borderColor: "var(--line-strong)" }}
          >
            {isAr ? "افتح المقال ←" : "Open article →"}
          </Link>
        )}
      </div>

      {result && (
        <div className="mt-6 p-5 border bg-slate-50" style={{ borderColor: "var(--line)" }}>
          <p className="eyebrow mb-2">{isAr ? "معاينة" : "Preview"}</p>
          <h4 className="font-display text-xl">{result.title}</h4>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-5">
            {result.preview}
          </p>
        </div>
      )}
    </div>
  );
}
